import { onBeforeUnmount, onMounted, ref, toValue, watch } from '#imports';
import type { Ref } from '#imports';

/**
 * 文件上传处理函数
 * @param files 文件列表
 */
export type FileUploadHandler = (files: File[]) => void;

/**
 * 文件上传配置选项
 */
export interface UseFileUploadOptions {
  /**
   * 文件上传处理函数
   */
  onFiles: FileUploadHandler;

  /**
   * 允许的文件类型（MIME类型）
   * @default ['image/*'] 默认允许所有图片类型
   */
  allowedTypes?: string[];

  /**
   * 最大文件大小（字节）
   * @default 10 * 1024 * 1024 (10MB)
   */
  maxFileSize?: number;

  /**
   * 是否启用拖拽功能
   * @default true
   */
  enableDrag?: boolean;

  /**
   * 拖拽区域元素引用
   */
  dragAreaRef?: () => HTMLElement|undefined;
  onDragOver?: (e: Event) => void;

}

/**
 * 文件上传 Hook
 * @param options 配置选项
 * @returns 拖拽状态和事件处理函数
 */
export function useDragFile(options: UseFileUploadOptions) {
  const {
    onFiles,
    allowedTypes = ['image/*'],
    maxFileSize = 10 * 1024 * 1024, // 10MB
    enableDrag = true,
    dragAreaRef,
    onDragOver,
  } = options;

  // 拖拽状态
  const isDragging = ref(false);
  const dragCounter = ref(0);

  // 处理拖拽事件
  const handleDragOver = (e: Event) => {
    const event = e as DragEvent;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    if (onDragOver) {
      onDragOver(e);
    }
  };

  const handleDragEnter = (e: Event) => {
    const event = e as DragEvent;
    event.preventDefault();
    event.stopPropagation();
    dragCounter.value++;
    isDragging.value = true;
  };

  const handleDragLeave = (e: Event) => {
    const event = e as DragEvent;
    event.preventDefault();
    event.stopPropagation();
    dragCounter.value--;
    if (dragCounter.value === 0) {
      isDragging.value = false;
    }
  };

  const handleDrop = (e: Event) => {
    const event = e as DragEvent;
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    dragCounter.value = 0;

    if (
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files.length > 0
    ) {
      const files = Array.from(event.dataTransfer.files);
      processFiles(files);
    }
  };

  // 处理粘贴事件
  const handlePaste = (e: ClipboardEvent) => {
    if (!e.clipboardData || !e.clipboardData.items) return;

    const items = Array.from(e.clipboardData.items);
    const fileItems = items.filter((item) => item.kind === 'file');

    if (fileItems.length > 0) {
      const files: File[] = [];

      fileItems.forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      });

      if (files.length > 0) {
        processFiles(files);
      }
    }
  };

  // 处理文件过滤和验证
  const processFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      // 检查文件类型
      const isTypeAllowed = allowedTypes.some((type) => {
        if (type.endsWith('/*')) {
          const category = type.split('/')[0];
          return file.type.startsWith(`${category}/`);
        }
        return file.type === type;
      });

      // 检查文件大小
      const isSizeValid = file.size <= maxFileSize;

      return isTypeAllowed && isSizeValid;
    });

    if (validFiles.length > 0) {
      onFiles(validFiles);
    }
  };

  const bindEvent = (target?: HTMLElement) => {
    target = target || toValue(dragAreaRef);
    if (!target) return;
    if (enableDrag) {
      target.addEventListener('dragover', handleDragOver);
      target.addEventListener('dragenter', handleDragEnter);
      target.addEventListener('dragleave', handleDragLeave);
      target.addEventListener('drop', handleDrop);
    }
  };

  const unbindEvent = (target?: HTMLElement) => {
    target = target || toValue(dragAreaRef);
    if (!target) return;
    target.removeEventListener('dragover', handleDragOver);
    target.removeEventListener('dragenter', handleDragEnter);
    target.removeEventListener('dragleave', handleDragLeave);
    target.removeEventListener('drop', handleDrop);
  };

  // 设置事件监听
 watch(() => toValue(dragAreaRef), (newTarget,oldTarget) => {
    if (oldTarget) {
      unbindEvent(oldTarget);
    }
    if (newTarget && enableDrag) {
      bindEvent(newTarget);
    }
    
  },{ immediate: true });

  // 清理事件监听
  onBeforeUnmount(() => {
    unbindEvent();
  });

  return {
    isDragging,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handlePaste,
  };
}
